import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';

const pastelPalette = [
    "#A7C7E7",
    "#BDE0FE",
    "#CDB4DB",
    "#F9C6C9",
    "#FFE5B4",
    "#F7D9C4",
    "#B7E4C7",
    "#C3E5AE",
    "#D4F1F4",
    "#FDE2E4"
];

const projectPalette = {
    stroke: "#059669",
    strokeHover: "#047857",
    assignedBase: "#34d399"
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const approxTextWidth = (text, fontSize) => (text?.length || 0) * fontSize * 0.58;
const areSetsEqual = (left, right) => {
    if (left === right) return true;
    const a = left instanceof Set ? left : new Set(left || []);
    const b = right instanceof Set ? right : new Set(right || []);
    if (a.size !== b.size) return false;
    for (const value of a) {
        if (!b.has(value)) return false;
    }
    return true;
};

const hashString = (value) => {
    const str = String(value || '');
    let hash = 0;
    for (let i = 0; i < str.length; i += 1) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash |= 0;
    }
    return Math.abs(hash);
};

const adjustColor = (colorValue, { h = 0, s = 0, l = 0 } = {}) => {
    const color = d3.hsl(colorValue);
    if (Number.isNaN(color.h)) return colorValue;
    color.h = (color.h + h + 360) % 360;
    color.s = clamp(color.s + s, 0, 1);
    color.l = clamp(color.l + l, 0, 1);
    return color.formatHex();
};

const isRoleNode = (node) => {
    if (!node?.data) return false;
    if (node.data.nodeType === "project") return false;
    const hasChildren = Boolean(node.children && node.children.length);
    return node.data.parent_id && !hasChildren;
};

const getNodeKind = (node) => node?.data?.nodeType || (isRoleNode(node) ? "role" : "circle");

const colorCache = new WeakMap();

const getNodeKey = (node) => {
    if (!node) return '';
    if (node?.data?.id !== undefined && node?.data?.id !== null) return `id:${node.data.id}`;
    if (node?.data?.name) return `name:${node.data.name}`;
    return `depth:${node.depth ?? 0}:${node.index ?? 0}`;
};

const deriveColorFromParent = (parentColor, nodeKey) => {
    const seed = hashString(nodeKey);
    const hueShift = (seed % 11) - 5;
    const lightShift = ((seed >> 3) % 8) - 4;
    const color = d3.hsl(parentColor);
    if (Number.isNaN(color.h)) return parentColor;
    color.h = (color.h + hueShift + 360) % 360;
    color.s = clamp(color.s + 0.02, 0.25, 0.55);
    color.l = clamp(color.l + lightShift / 100, 0.78, 0.92);
    return color.formatHex();
};

const getNodeBaseColor = (node) => {
    if (!node) return pastelPalette[0];
    if (colorCache.has(node)) return colorCache.get(node);
    const nodeKey = getNodeKey(node);
    let base = node?.data?.color;
    if (base && !d3.color(base)) {
        base = null;
    }
    if (!base) {
        if (node?.parent) {
            base = deriveColorFromParent(getNodeBaseColor(node.parent), nodeKey);
        } else {
            base = pastelPalette[hashString(nodeKey) % pastelPalette.length];
        }
    }
    colorCache.set(node, base);
    return base;
};

const getNodePalette = (node) => {
    const kind = getNodeKind(node);
    if (kind === "project") return projectPalette;
    const base = getNodeBaseColor(node);
    const isRoleLike = kind === "role";
    const toned = adjustColor(base, {
        s: isRoleLike ? 0.08 : 0.02,
        l: isRoleLike ? 0.04 : 0.02
    });
    return {
        assignedBase: toned,
        stroke: adjustColor(toned, { l: -0.18, s: 0.08 }),
        strokeHover: adjustColor(toned, { l: -0.26, s: 0.1 })
    };
};

const CirclePack = ({ data, onCircleClick, openMeetingCircleIds, valueAccessor, tight = false, selectedCircleId }) => {
    const svgRef = useRef(null);
    const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
    const meetingIdsRef = useRef(new Set());
    const meetingStroke = "#10b981";
    const focusIdRef = useRef(null);
    const zoomRef = useRef(null);
    const nodesByIdRef = useRef(new Map());
    const selectedIdRef = useRef(null);
    const selectedCircleIdRef = useRef(null);
    selectedCircleIdRef.current = selectedCircleId;
    const lowPowerDevice = useMemo(() => {
        if (typeof navigator === "undefined") return false;
        const cores = Number(navigator.hardwareConcurrency || 0);
        if (!Number.isFinite(cores) || cores <= 0) return false;
        return cores <= 4;
    }, []);

    useEffect(() => {
        const parent = svgRef.current?.parentElement;
        if (!parent) return undefined;
        let frame;
        const updateDimensions = () => {
            const rect = parent.getBoundingClientRect();
            const nextWidth = Math.max(0, Math.floor(rect.width));
            const nextHeight = Math.max(0, Math.floor(rect.height)) || 600;
            setDimensions((prev) => {
                if (prev.width === nextWidth && prev.height === nextHeight) return prev;
                return { width: nextWidth, height: nextHeight };
            });
        };
        updateDimensions();

        if (typeof ResizeObserver !== "undefined") {
            const observer = new ResizeObserver(() => {
                if (frame) cancelAnimationFrame(frame);
                frame = requestAnimationFrame(updateDimensions);
            });
            observer.observe(parent);
            return () => {
                observer.disconnect();
                if (frame) cancelAnimationFrame(frame);
            };
        }

        window.addEventListener("resize", updateDimensions);
        return () => {
            window.removeEventListener("resize", updateDimensions);
            if (frame) cancelAnimationFrame(frame);
        };
    }, []);

    useEffect(() => {
        if (!data || !svgRef.current) return;

        const { width, height } = dimensions;
        if (!width || !height || width < 10 || height < 10) return; // skip if container collapsed

        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();

        try {
        const marginScale = tight ? 0.02 : 0.04;
        const margin = Math.max(Math.min(width, height) * marginScale, 4); // Dynamic margin with minimum
        const displayWidth = Math.max(width - margin * 2, 10);
        const displayHeight = Math.max(height - margin * 2, 10);

        // --- DATA PROCESSING ---
        const safeValueAccessor = valueAccessor || (() => 1);
        const shouldScaleRoles = !valueAccessor;
        const roleDepthScale = (node) => {
            if (!shouldScaleRoles) return 1;
            const isRoleLike = isRoleNode(node) || node?.data?.nodeType === "project";
            if (!isRoleLike) return 1;
            if (node.parent && node.parent.depth === 0) return 2;
            const depth = Number(node.depth || 0);
            const scale = 1.05 - depth * 0.05;
            return Math.max(0.9, scale);
        };

        const root = d3.hierarchy(data);
        root.eachAfter((node) => {
            if (!node.children || node.children.length === 0) {
                const raw = safeValueAccessor(node.data);
                const value = Number(raw);
                const base = Number.isNaN(value) ? 0 : Math.max(0, value);
                node.value = base * roleDepthScale(node);
                return;
            }
            node.value = d3.sum(node.children, (child) => child.value || 0);
        });
        root.sort((a, b) => b.value - a.value);
        const nodeCount = root.descendants().length;
        const performanceMode = lowPowerDevice || nodeCount > 120;

        // --- FILTERS & DEFINITIONS ---
        if (!performanceMode) {
            const defs = svg.append("defs");
            const shadowFilter = defs.append("filter")
                .attr("id", "premium-shadow")
                .attr("height", "200%");

            shadowFilter.append("feGaussianBlur")
                .attr("in", "SourceAlpha")
                .attr("stdDeviation", 8)
                .attr("result", "blur");

            shadowFilter.append("feOffset")
                .attr("in", "blur")
                .attr("dx", 0)
                .attr("dy", 8)
                .attr("result", "offsetBlur");

            shadowFilter.append("feFlood")
                .attr("flood-color", "rgba(0,0,0,0.15)")
                .attr("result", "color");

            shadowFilter.append("feComposite")
                .attr("in", "color")
                .attr("in2", "offsetBlur")
                .attr("operator", "in")
                .attr("result", "shadow");

            const feMerge = shadowFilter.append("feMerge");
            feMerge.append("feMergeNode").attr("in", "shadow");
            feMerge.append("feMergeNode").attr("in", "SourceGraphic");
        }
        const nodesById = new Map();
        root.descendants().forEach((node) => {
            if (node?.data?.id !== undefined && node?.data?.id !== null) {
                nodesById.set(String(node.data.id), node);
            }
        });
        nodesByIdRef.current = nodesById;

        const getPadding = (d) => {
            // Slightly higher padding near the root keeps siblings away from the shell
            if (d.depth === 0) return tight ? 12 : 22;
            // Compact padding once we are deeper to preserve room for labels
            return d.children && d.children.length > 0 ? (tight ? 8 : 14) : (tight ? 5 : 8);
        };

        const pack = d3.pack()
            .size([displayWidth, displayHeight])
            .padding(getPadding);

        pack(root);
        // --- POST-PACK LAYOUT ADJUSTMENT ---
        const shiftChildren = (node) => {
            if (!node.children || node.children.length === 0) return;

            let maxChildBottom = -Infinity;
            node.children.forEach(child => {
                maxChildBottom = Math.max(maxChildBottom, child.y + child.r);
            });

            const parentBottom = node.y + node.r;
            const targetBottom = parentBottom - margin;
            const shiftAmount = targetBottom - maxChildBottom;
            const cappedShift = Math.min(shiftAmount, node.r * 0.08);

            if (cappedShift > 0) {
                const applyShift = (n, dy) => {
                    n.y += dy;
                    if (n.children) {
                        n.children.forEach(child => applyShift(child, dy));
                    }
                };

                node.children.forEach(child => applyShift(child, cappedShift));
            }

            node.children.forEach(shiftChildren);
        };

        shiftChildren(root);

        const keepChildrenInsideParent = (node) => {
            if (!node.children || node.children.length === 0) return;

            node.children.forEach(keepChildrenInsideParent);

            let maxExtent = 0;
            node.children.forEach(child => {
                const distanceToCenter = Math.hypot(child.x - node.x, child.y - node.y);
                maxExtent = Math.max(maxExtent, distanceToCenter + child.r);
            });

            const safetyMargin = Math.max(3, node.r * 0.012);
            const allowedRadius = Math.max(node.r - safetyMargin, 0);

            if (maxExtent > allowedRadius && maxExtent > 0) {
                const scale = allowedRadius / maxExtent;
                const scaleBranch = (n) => {
                    n.x = node.x + (n.x - node.x) * scale;
                    n.y = node.y + (n.y - node.y) * scale;
                    n.r *= scale;
                    if (n.children) {
                        n.children.forEach(scaleBranch);
                    }
                };
                node.children.forEach(scaleBranch);
            }
        };

        keepChildrenInsideParent(root);

        // --- RENDERING ---
        const g = svg.append("g")
            .attr("transform", `translate(${width / 2},${height / 2})`);

        let view;
        let focus = root;
        if (selectedCircleIdRef.current !== undefined && selectedCircleIdRef.current !== null) {
            focusIdRef.current = String(selectedCircleIdRef.current);
        }
        if (!focusIdRef.current && root?.data?.id !== undefined) {
            focusIdRef.current = String(root.data.id);
        }

        const hasMeeting = (node) => node?.data?.id !== undefined && meetingIdsRef.current.has(String(node.data.id));
        const isProjectNode = (node) => node?.data?.nodeType === "project";
        const hasAssignee = (node) => {
            if (isProjectNode(node)) return true;
            const ownerId = node?.data?.owner_id;
            if (ownerId !== null && ownerId !== undefined && ownerId !== '') return true;
            const ownerIds = node?.data?.owner_ids;
            return Array.isArray(ownerIds) && ownerIds.some((id) => id !== null && id !== undefined && id !== '');
        };

        // Color Palette - Circles vs Roles
        const colors = {
            meetingStroke: meetingStroke, // Emerald 500
            text: "#1e293b", // Slate 800
            textLight: "#475569" // Slate 600
        };

        const baseAlpha = 0.1;
        const alphaStep = 0.07;
        const maxAlpha = 0.65;
        const getAssignedFill = (d, boost = 0) => {
            const alpha = Math.min(maxAlpha, baseAlpha + d.depth * alphaStep + boost);
            const color = d3.color(getNodePalette(d).assignedBase);
            if (color) {
                color.opacity = alpha;
                return color.formatRgb();
            }
            return getNodePalette(d).assignedBase;
        };

        const getUnassignedFill = (d) => {
            if (getNodeKind(d) === "project") return "#ecfdf3";
            const base = getNodePalette(d).assignedBase;
            const color = d3.color(base);
            if (color) {
                color.opacity = isRoleNode(d) ? 0.1 : 0.07;
                return color.formatRgb();
            }
            return base;
        };

        const getHoverFill = (d) => {
            if (hasAssignee(d)) return getAssignedFill(d, 0.1);
            const base = getNodePalette(d).assignedBase;
            return adjustColor(base, { l: 0.06, s: 0.02 });
        };

        const getBaseFill = (d) => {
            if (!hasAssignee(d)) return getUnassignedFill(d);
            return getAssignedFill(d);
        };

        // Helper to update styles based on state
        const updateCircleStyles = () => {
            const styled = performanceMode
                ? circle
                : circle.transition().duration(300);
            styled
                .style("stroke", d => hasMeeting(d) ? colors.meetingStroke : getNodePalette(d).stroke)
                .style("stroke-width", d => d.depth === 0 ? 4 : (d.children ? 2.5 : 1.5))
                .style("fill", d => {
                    if (d === focus) return hasAssignee(d) ? getAssignedFill(d, 0.08) : getUnassignedFill(d);
                    return getBaseFill(d);
                });
        };

        // Circles
        const circle = g.selectAll("circle")
            .data(root.descendants())
            .join("circle")
            .attr("r", d => d.r)
            .style("fill", d => getBaseFill(d))
            .style("stroke", d => hasMeeting(d) ? colors.meetingStroke : getNodePalette(d).stroke)
            .style("stroke-width", d => d.depth === 0 ? 4 : (d.children ? 2.5 : 1.5))
            .style("filter", d => (!performanceMode && d.depth > 0 ? "url(#premium-shadow)" : "none"))
            .style("cursor", "pointer")
            .style("transition", performanceMode ? "none" : "stroke 0.3s ease, stroke-width 0.3s ease, fill 0.3s ease")
            .on("click", (event, d) => {
                if (focus !== d) {
                    zoom(event, d);
                }
                event.stopPropagation();
                if (onCircleClick) {
                    onCircleClick(d.data);
                }
            });
        if (!performanceMode) {
            circle
                .on("mouseover", function (event, d) {
                    if (d !== focus) {
                        d3.select(this)
                            .style("stroke", hasMeeting(d) ? colors.meetingStroke : getNodePalette(d).strokeHover)
                            .style("stroke-width", d.depth === 0 ? 6 : (d.children ? 4 : 3))
                            .style("fill", getHoverFill(d));
                    }
                })
                .on("mouseout", function (event, d) {
                    if (d !== focus) {
                        d3.select(this)
                            .style("stroke", hasMeeting(d) ? colors.meetingStroke : getNodePalette(d).stroke)
                            .style("stroke-width", d.depth === 0 ? 4 : (d.children ? 2.5 : 1.5))
                            .style("fill", getBaseFill(d));
                    }
                });
        }

        // Labels
        const labelGroup = g.append("g").style("pointer-events", "none");

        const label = labelGroup.selectAll("text")
            .data(root.descendants())
            .join("text")
            // Always visible logic
            .style("fill-opacity", 1)
            .style("display", "inline")
            .attr("text-anchor", "middle")
            .style("font-family", "'Inter', system-ui, -apple-system, sans-serif")
            .style("font-weight", d => d.children ? "700" : "600")
            .style("fill", colors.text)
            .style("text-shadow", performanceMode ? "none" : "0 2px 4px rgba(255,255,255,0.9)");

        const getLabelFontSize = (node) => {
            if (node.children && node.children.length > 0) {
                return Math.min(26, Math.max(14, node.r / 4.6));
            }
            return Math.min(20, Math.max(10, node.r / 2.9));
        };

        const getLabelPadding = (radius, isParent) => {
            const safeRadius = Number(radius || 0);
            if (isParent) {
                return Math.min(18, Math.max(6, safeRadius * 0.12));
            }
            return Math.min(12, Math.max(4, safeRadius * 0.18));
        };

        const measureLine = (textEl, content) => {
            const tester = textEl.append("tspan").attr("x", 0).attr("y", 0).text(content);
            const length = tester.node()?.getComputedTextLength() || 0;
            tester.remove();
            return length;
        };

        const splitWord = (textEl, word, maxWidth) => {
            if (!word) return [];
            if (measureLine(textEl, word) <= maxWidth) return [word];
            const parts = [];
            let chunk = '';
            for (const char of word) {
                const candidate = `${chunk}${char}`;
                if (chunk && measureLine(textEl, candidate) > maxWidth) {
                    parts.push(chunk);
                    chunk = char;
                } else {
                    chunk = candidate;
                }
            }
            if (chunk) parts.push(chunk);
            return parts.length ? parts : [word];
        };

        const wrapLines = (textEl, words, maxWidth) => {
            const lines = [];
            let current = '';
            words.forEach((word) => {
                const segments = splitWord(textEl, word, maxWidth);
                segments.forEach((segment) => {
                    const candidate = current ? `${current} ${segment}` : segment;
                    if (!current || measureLine(textEl, candidate) <= maxWidth) {
                        current = candidate;
                    } else {
                        if (current) lines.push(current);
                        current = segment;
                    }
                });
            });
            if (current) lines.push(current);
            return lines;
        };

        const applyLabelLayout = (scale) => {
            const labelScale = scale;
            if (performanceMode) {
                label.each(function (d) {
                    const textEl = d3.select(this);
                    const name = d?.data?.name || '';
                    if (!name) {
                        textEl.text('');
                        return;
                    }
                    const radius = d.r * scale;
                    if (radius < 14) {
                        textEl.text('');
                        return;
                    }
                    const fontSize = Math.max(7, Math.min(d.children ? 18 : 14, radius / (d.children ? 4.8 : 3.4)));
                    const maxWidth = Math.max(10, radius * 1.6);
                    const maxChars = Math.max(1, Math.floor(maxWidth / Math.max(approxTextWidth('M', fontSize), 1)));
                    const text = name.length > maxChars ? `${name.slice(0, Math.max(1, maxChars - 3))}...` : name;
                    textEl
                        .text(text)
                        .style("font-size", `${fontSize}px`)
                        .attr("dominant-baseline", "middle");
                    d.__labelLayout = { simple: true, fontSize, scale };
                });
                return;
            }
            label.each(function (d) {
                const textEl = d3.select(this);
                const name = d?.data?.name || '';
                if (!name) {
                    textEl.text('');
                    return;
                }

                const radius = d.r * scale;
                const isParent = Boolean(d.children && d.children.length);
                const padding = getLabelPadding(radius, isParent);
                const maxWidth = Math.max(0, radius * 2 - padding * 2);
                const maxHeight = Math.max(0, radius * 2 - padding * 2);
                const lineHeight = 1.1;
                const minFontSize = (d.children ? 10 : 7) * 0.5 * Math.min(1, labelScale);
                let fontSize = Math.max(minFontSize, getLabelFontSize(d) * labelScale);
                const words = name.split(/\s+/).filter(Boolean);
                if (!maxWidth || !maxHeight || !words.length) {
                    textEl.text('');
                    return;
                }

                let lines = [];
                let guard = 0;
                while (guard < 14) {
                    textEl.style("font-size", `${fontSize}px`);
                    const maxWordWidth = words.reduce(
                        (max, word) => Math.max(max, measureLine(textEl, word)),
                        0
                    );
                    if (maxWordWidth > maxWidth && fontSize > minFontSize) {
                        fontSize -= 1;
                        guard += 1;
                        continue;
                    }
                    const maxLines = Math.max(1, Math.floor(maxHeight / (fontSize * lineHeight)));
                    lines = wrapLines(textEl, words, maxWidth);
                    const maxLineWidth = lines.reduce(
                        (max, line) => Math.max(max, measureLine(textEl, line)),
                        0
                    );
                    if (lines.length <= maxLines && maxLineWidth <= maxWidth) break;
                    if (fontSize <= minFontSize) break;
                    fontSize -= 1;
                    guard += 1;
                }

                const maxLines = Math.max(1, Math.floor(maxHeight / (fontSize * lineHeight)));
                if (lines.length > maxLines) {
                    lines = lines.slice(0, maxLines);
                    const lastIndex = lines.length - 1;
                    if (lastIndex >= 0) {
                        const last = lines[lastIndex];
                        const trimmed = last.trim();
                        const shortened = trimmed.length > 2 ? `${trimmed.slice(0, -1)}...` : trimmed;
                        lines[lastIndex] = shortened || trimmed;
                    }
                }

                if (!lines.length) {
                    textEl.text('');
                    return;
                }

                textEl.text(null).style("font-size", `${fontSize}px`);

                const totalHeight = (lines.length - 1) * lineHeight * fontSize;
                const baseY = isParent
                    ? (-radius + padding + fontSize)
                    : (-totalHeight / 2);

                textEl.attr("dominant-baseline", "alphabetic");
                lines.forEach((line, index) => {
                    textEl.append("tspan")
                        .attr("x", 0)
                        .attr("y", baseY)
                        .attr("dy", `${index * lineHeight}em`)
                        .text(line);
                });

                d.__labelLayout = {
                    lines,
                    fontSize,
                    baseY,
                    lineHeight,
                    scale
                };
            });
        };

        const applyLabelScale = (scale) => {
            label.each(function (d) {
                const layout = d.__labelLayout;
                if (!layout) return;
                const factor = layout.scale ? (scale / layout.scale) : 1;
                const textEl = d3.select(this);
                if (layout.simple) {
                    textEl.style("font-size", `${layout.fontSize * factor}px`);
                    return;
                }
                textEl.style("font-size", `${layout.fontSize * factor}px`);
                textEl.selectAll("tspan").attr("y", layout.baseY * factor);
            });
        };

        // Zoom Logic
        const zoomTo = (v, applyLabels = true) => {
            const k = Math.min(displayWidth, displayHeight) / v[2];
            view = v;
            label.attr("transform", d => `translate(${(d.x - v[0]) * k},${(d.y - v[1]) * k})`);
            if (applyLabels) {
                applyLabelLayout(k);
            } else {
                applyLabelScale(k);
            }

            circle.attr("transform", d => `translate(${(d.x - v[0]) * k},${(d.y - v[1]) * k})`);
            circle.attr("r", d => d.r * k);
        };

        const zoom = (event, d) => {
            focus = d;
            focusIdRef.current = d?.data?.id !== undefined ? String(d.data.id) : null;
            selectedIdRef.current = focusIdRef.current;
            updateCircleStyles(); // Update styles to highlight the new focus

            const targetSize = focus.r * 1.85;
            const startView = view || [focus.x, focus.y, targetSize];
            if (performanceMode) {
                zoomTo([focus.x, focus.y, targetSize], false);
                return;
            }
            svg.transition()
                .duration(700)
                .ease(d3.easeCubicOut)
                .tween("zoom", () => {
                    const i = d3.interpolateZoom(startView, [focus.x, focus.y, targetSize]);
                    return t => zoomTo(i(t), false);
                });
        };
        zoomRef.current = (id) => {
            const target = nodesByIdRef.current.get(String(id));
            if (target) {
                zoom(null, target);
            }
        };

        // Initial Zoom
        const initialFocus = root.descendants().find(
            (node) => focusIdRef.current && String(node.data?.id) === String(focusIdRef.current)
        ) || root;
        focus = initialFocus;
        updateCircleStyles(); // Initial style application
        const initialSize = tight ? focus.r * 1.85 : (focus.r * 2 + margin);
        zoomTo([focus.x, focus.y, initialSize]);

        // Background Click
        svg.on("click", (event) => {
            zoom(event, root);
            focusIdRef.current = root?.data?.id !== undefined ? String(root.data.id) : null;
            if (onCircleClick) onCircleClick(root.data);
        });

        } catch (err) {
            console.error("CirclePack render error", err);
        }

    }, [data, dimensions, onCircleClick, valueAccessor, tight, lowPowerDevice]);

    useEffect(() => {
        if (selectedCircleId === undefined || selectedCircleId === null) return;
        const id = String(selectedCircleId);
        if (selectedIdRef.current === id) return;
        if (!zoomRef.current) return;
        selectedIdRef.current = id;
        zoomRef.current(id);
    }, [selectedCircleId]);

    useEffect(() => {
        const nextSet = openMeetingCircleIds instanceof Set
            ? openMeetingCircleIds
            : new Set(openMeetingCircleIds || []);
        meetingIdsRef.current = nextSet;
        if (!svgRef.current) return;
        const svg = d3.select(svgRef.current);
        svg.selectAll("circle")
            .style("stroke", d => (
                meetingIdsRef.current.has(String(d.data.id))
                    ? meetingStroke
                    : getNodePalette(d).stroke
            ));
    }, [openMeetingCircleIds]);

    return (
        <div className="w-full h-full flex justify-center items-center overflow-hidden relative bg-slate-50/50">
            <svg
                ref={svgRef}
                width={dimensions.width}
                height={dimensions.height}
                style={{ display: 'block', maxWidth: '100%', maxHeight: '100%' }}
            />
        </div>
    );
};

const MemoizedCirclePack = React.memo(
    CirclePack,
    (prevProps, nextProps) => (
        prevProps.data === nextProps.data &&
        prevProps.onCircleClick === nextProps.onCircleClick &&
        prevProps.valueAccessor === nextProps.valueAccessor &&
        prevProps.tight === nextProps.tight &&
        String(prevProps.selectedCircleId ?? '') === String(nextProps.selectedCircleId ?? '') &&
        areSetsEqual(prevProps.openMeetingCircleIds, nextProps.openMeetingCircleIds)
    )
);

export default MemoizedCirclePack;
